/*!
 * 實體獎勵確認（v2.50.0）—— 「聯盟 P&L 調整」頁的分頁
 *
 * 每週：輸入本週關鍵字 → 系統撈出名稱含關鍵字的 MTT → Apex 逐場確認「是實體獎勵 → 扣除」或「不是」。
 * 確認扣除才寫進 P&L；沒做完這一步，第二段產報表會被擋（沒有實體獎勵賽要按「本週沒有實體獎勵賽」）。
 * 規則與閘門見 Backend/services/tiny/physical_prize_service.py。
 */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Input, Table } from "reactstrap";
import axios from "axios";

import TinyLoading, { TinyBusy } from "components/tiny/TinyLoading";
import { COLORS, thStyle, tdStyle, softBadge, mutedBadge, inputStyle } from "constant/adminTheme";

const API = "/api/tiny/pl-adjustments/prize";
const fmt = (v) => Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
const apiError = (e) => e?.response?.data?.msg || e?.message || "發生錯誤";
const TYPE_LABELS = { credit: "信用制", deposit: "儲值制" };

function EventCard({ ev, week, frozen, onData, setError }) {
  const [picks, setPicks] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const init = {};
    ev.winners.forEach((w) => {
      init[`${w.club_id}|${w.player_id}`] = { on: w.suggest, amount: w.prize };
    });
    setPicks(init);
  }, [ev]);

  const chosen = ev.winners.filter((w) => !w.bot && picks[`${w.club_id}|${w.player_id}`]?.on);
  const total = chosen.reduce((s, w) => s + Number(picks[`${w.club_id}|${w.player_id}`]?.amount || 0), 0);

  const post = (path, body) => {
    setBusy(true);
    axios.post(`${API}/${week}/${path}`, body)
      .then((res) => {
        if (res.data?.code !== 1) throw new Error(res.data?.msg);
        onData(res.data.data);
        setError(null);
      })
      .catch((e) => setError(apiError(e)))
      .finally(() => setBusy(false));
  };

  const dec = ev.decision;
  return (
    <div style={{ border: `1px solid ${dec ? COLORS.border : "rgba(251,99,64,0.5)"}`, borderRadius: 8,
                  padding: 14, marginBottom: 14, background: COLORS.cardBg }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{ev.mtt_name}</span>
          <span style={{ color: COLORS.textMuted, marginLeft: 10 }}>{ev.start_time.slice(5, 16)}</span>
          <span style={{ ...mutedBadge, marginLeft: 10 }}>關鍵字：{ev.matched.join("、")}</span>
          <span style={{ ...mutedBadge, marginLeft: 6 }}>實體面額（最常見獎金）{fmt(ev.face)}</span>
        </div>
        {dec ? (
          <div>
            {dec.is_prize
              ? <span style={softBadge(COLORS.info)}>已確認扣除 {dec.count} 人，共 {fmt(dec.total)}</span>
              : <span style={mutedBadge}>已確認：不是實體獎勵</span>}
            <span style={{ color: COLORS.textMuted, fontSize: 12, marginLeft: 8 }}>{dec.by}</span>
            {!frozen && (
              <Button size="sm" color="link" style={{ color: COLORS.danger }} disabled={busy}
                      onClick={() => post("undo", { key: ev.key })}>撤銷</Button>
            )}
          </div>
        ) : (
          <span style={softBadge(COLORS.warning)}>待確認</span>
        )}
      </div>

      {!dec && (
        <>
          <div style={{ overflowX: "auto", marginTop: 10 }}>
            <Table size="sm" style={{ color: COLORS.textPrimary, minWidth: 760 }}>
              <thead>
                <tr>
                  <th style={thStyle}>扣</th>
                  <th style={thStyle}>玩家</th>
                  <th style={thStyle}>俱樂部</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>這場獎金</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>要扣的金額</th>
                  <th style={thStyle}>備註</th>
                </tr>
              </thead>
              <tbody>
                {ev.winners.map((w) => {
                  const k = `${w.club_id}|${w.player_id}`;
                  const p = picks[k] || {};
                  return (
                    <tr key={k} style={{ opacity: w.bot ? 0.45 : 1 }}>
                      <td style={tdStyle}>
                        <Input type="checkbox" style={{ position: "static", margin: 0 }} disabled={!!w.bot || frozen}
                               checked={!w.bot && !!p.on}
                               onChange={() => setPicks({ ...picks, [k]: { ...p, on: !p.on } })} />
                      </td>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 600 }}>{w.nickname}</div>
                        <div style={{ fontFamily: "monospace", fontSize: 11, color: COLORS.textMuted }}>{w.player_id}</div>
                      </td>
                      <td style={tdStyle}>
                        {w.club_name}
                        <span style={{ color: COLORS.textDimmed, fontSize: 11, marginLeft: 6 }}>{TYPE_LABELS[w.club_type] || ""}</span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>{fmt(w.prize)}</td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        <Input type="number" bsSize="sm" disabled={!!w.bot || !p.on || frozen}
                               style={{ ...inputStyle, width: 120, marginLeft: "auto", textAlign: "right" }}
                               value={p.amount ?? ""} max={w.prize} min={0}
                               onChange={(e) => setPicks({ ...picks, [k]: { ...p, amount: e.target.value } })} />
                      </td>
                      <td style={{ ...tdStyle, fontSize: 12, color: COLORS.textMuted }}>
                        {w.bot ? `${w.bot}：不扣` : (w.suggest ? "" : "獎金和實體面額不同（零頭？）預設不扣")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
          {!frozen && (
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", alignItems: "center" }}>
              <Button color="info" disabled={busy || chosen.length === 0}
                      onClick={() => post("decide", {
                        key: ev.key, is_prize: true,
                        deductions: chosen.map((w) => ({ player_id: w.player_id, club_id: w.club_id,
                                                         amount: Number(picks[`${w.club_id}|${w.player_id}`].amount) })),
                      })}>
                {busy ? <TinyBusy light style={{ marginRight: 4 }} /> : null}
                是實體獎勵：扣除勾選的 {chosen.length} 人（共 {fmt(total)}）
              </Button>
              <Button color="secondary" disabled={busy} onClick={() => post("decide", { key: ev.key, is_prize: false })}>
                不是實體獎勵
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

EventCard.propTypes = {
  ev: PropTypes.object.isRequired, week: PropTypes.string.isRequired, frozen: PropTypes.bool,
  onData: PropTypes.func.isRequired, setError: PropTypes.func.isRequired,
};

function TinyPhysicalPrizeReview({ week, onGate, refreshKey, compact }) {
  const [data, setDataRaw] = useState(null);
  // 每次資料更新都把閘門狀態回報給上層（報表整合中心用它決定第 2 段能不能按）
  const setData = (d) => {
    setDataRaw(d);
    if (onGate) onGate(d ? d.gate : null);
  };
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!week) return;
    setDataRaw(null);
    axios.get(`${API}/${week}`)
      .then((res) => {
        if (res.data?.code !== 1) throw new Error(res.data?.msg);
        setData(res.data.data);
        setText((res.data.data.keywords || []).join("、"));
        setError(null);
      })
      .catch((e) => setError(apiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [week, refreshKey]);

  const save = (noEvent) => {
    setBusy(true);
    const keywords = noEvent ? [] : text.split(/[,，、\s]+/).map((s) => s.trim()).filter(Boolean);
    axios.post(`${API}/${week}/keywords`, { keywords, no_event: noEvent })
      .then((res) => {
        if (res.data?.code !== 1) throw new Error(res.data?.msg);
        setData(res.data.data);
        setError(null);
      })
      .catch((e) => setError(apiError(e)))
      .finally(() => setBusy(false));
  };

  if (!data && !error) return <TinyLoading text="讀取實體獎勵確認" />;
  const gate = data?.gate;

  return (
    <div>
      {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}
      {gate && !compact && (
        <div style={{ ...(gate.ok ? softBadge(COLORS.success) : softBadge(COLORS.warning)),
                      display: "block", fontSize: 13, padding: "10px 14px", marginBottom: 14 }}>
          {gate.ok ? "✓ " : "⚠ 產報表前要完成："}{gate.reason}
        </div>
      )}
      {data?.frozen && (
        <div style={{ ...mutedBadge, display: "block", padding: "8px 12px", marginBottom: 14 }}>
          這週的報表已寄出（凍結），這裡只能看、不能改。
        </div>
      )}

      {data && !data.frozen && (
        <div style={{ background: COLORS.cardBg, borderRadius: 8, padding: 14, marginBottom: 16,
                      border: `1px solid ${COLORS.border}` }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>本週關鍵字</div>
          <div style={{ color: COLORS.textMuted, fontSize: 12, marginBottom: 8 }}>
            每週都要重新輸入。這週有實體獎勵賽（iPhone、Matebook、實體門票…），就把賽事名稱裡的字填進來，多個用逗號或頓號分開；
            沒有的話按「本週沒有實體獎勵賽」。
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="例如：iPhone、Matebook"
                   style={{ ...inputStyle, maxWidth: 420 }} />
            <Button color="info" disabled={busy || !text.trim()} onClick={() => save(false)}>
              {busy ? <TinyBusy light style={{ marginRight: 4 }} /> : null}儲存並找出賽事
            </Button>
            <Button color="secondary" disabled={busy} onClick={() => save(true)}>本週沒有實體獎勵賽</Button>
          </div>
          {data.no_event && <div style={{ color: COLORS.success, fontSize: 12, marginTop: 8 }}>已標記：本週沒有實體獎勵賽</div>}
        </div>
      )}

      {data && data.keywords.length > 0 && data.events.length === 0 && (
        <div style={{ color: COLORS.textMuted, padding: "8px 0 16px" }}>
          關鍵字「{data.keywords.join("、")}」這週沒有找到任何有得主的賽事。確認關鍵字有沒有打對；真的沒有就按「本週沒有實體獎勵賽」。
        </div>
      )}
      {data && data.events.map((ev) => (
        <EventCard key={ev.key} ev={ev} week={week} frozen={data.frozen} onData={setData} setError={setError} />
      ))}
    </div>
  );
}

TinyPhysicalPrizeReview.propTypes = {
  week: PropTypes.string, onGate: PropTypes.func, refreshKey: PropTypes.any, compact: PropTypes.bool,
};

export default TinyPhysicalPrizeReview;
